import { FC } from "react";

import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { type PresentationBannerColorType } from "components/footer/PresentationBanner";
import { t } from "i18n/i18n";
import { PillButton } from "mds/components/PillButton";
import { useCourseCapabilitiesByCourseId } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectClassSessionByCourseId,
  selectCurrentCourseId,
  selectCurrentUserId,
} from "store/selectors";
import { useJoinAsCourseAdmin } from "utils/hooks/use-join-as-section-admin";
import { setPresentationWindow, takeOverPresenting } from "utils/presentation";
import { Link } from "utils/routing";
import { pageUrl, withQueryParams } from "utils/urls";

interface TakeOverPresentationButtonProps {
  color: PresentationBannerColorType;
  courseId?: string;
}

export const TakeOverPresentationButton: FC<TakeOverPresentationButtonProps> = ({
  color,
  courseId: courseIdProp,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const currentCourseId = useAppSelector(selectCurrentCourseId);
  const courseId = courseIdProp || currentCourseId;
  const classSession = useAppSelector((state) => selectClassSessionByCourseId(state, courseId));
  const presentedPageId = classSession?.presented_page_id;
  const isPresentingUser = classSession?.presenting_user_id === userId;
  const { joinAsCourseAdminIfNew } = useJoinAsCourseAdmin();
  const { canAuthorCourse } = useCourseCapabilitiesByCourseId(courseId);

  if (!courseId || !presentedPageId || !canAuthorCourse || isPresentingUser) {
    return null;
  }

  const presentedPageUrl = withQueryParams(pageUrl(courseId, presentedPageId), {
    [FOLLOWING_QUERY_PARAM]: "1",
  });

  const onTakeOverPresentation = async () => {
    await joinAsCourseAdminIfNew();

    setPresentationWindow();
    return takeOverPresenting(classSession?.id, userId);
  };

  return (
    <PillButton
      as={Link}
      eventName="Presentation - Take over presentation"
      kind={`banner-${color}`}
      size="s"
      to={presentedPageUrl}
      onClick={onTakeOverPresentation}
    >
      {t("main_header.take_over_presentation")}
    </PillButton>
  );
};
